import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  CatalogosHistorial,
  FilaHistorial,
  FiltrosHistorial,
  OperationsHistoryService,
  PaginaHistorial,
  TotalHistorial,
} from '../../services/operations-history.service';

/** Los filtros tal como los edita la pantalla. */
interface EstadoFiltros {
  desde: string;
  hasta: string;
  usuario: string;
  operador: string;
  grupos: string[];
  estados: string[];
  fuentes: string[];
  tipos: string[];
  moneda: string;
  baseFecha: 'movimiento' | 'termino';
  orden: 'asc' | 'desc';
}

/** Etiquetas de los buckets. Las claves son las que manda el backend. */
const NOMBRE_GRUPO: Record<string, string> = {
  CRIPTO: 'USDT',
  FIAT: 'FIAT VES',
  ZELLE: 'Zelle',
  RETIRO: 'Retiro FIAT',
  AC: 'Aguacate Cambios',
  SWAP_USDT_VES: 'Swap USDT/VES',
  RETIRO_VES: 'Retiro VES',
  REMESA_INTL: 'Remesas COP/PEN',
  WALLET_USDT: 'Wallet USDT',
  WALLET_VES: 'Wallet VES',
  BONO: 'Bonos y referidos',
  FONDEO: 'Cargas de saldo',
  AJUSTE: 'Ajustes manuales',
};

const NOMBRE_FUENTE: Record<string, string> = {
  TRANSACCION: 'Transacciones',
  WALLET_USDT: 'Cardex USDT',
  WALLET_VES: 'Cardex VES',
  BALANCE: 'Libro de saldos CLP',
};

/** Monedas sin decimales: mostrarlas con dos solo ensucia la lectura. */
const SIN_DECIMALES = ['CLP', 'VES', 'COP', 'PEN'];

/**
 * Grupos que NO son plata que sale del saldo CLP del cliente: entradas
 * (cargas), correcciones (ajustes), los wallets en otra moneda y los premios.
 */
const NO_SON_SALIDAS = ['FONDEO', 'AJUSTE', 'WALLET_USDT', 'WALLET_VES', 'BONO'];

/** Estados terminados. Todo lo demas cuenta como "en proceso". */
const ESTADOS_CERRADOS = ['ENVIADA', 'RECHAZADA', 'ANULADA'];

/** Lo que muestra la franja de totales, ya calculado. Montos en CLP. */
interface ResumenTotales {
  cargas: { cantidad: number; monto: number };
  salidas: { cantidad: number; monto: number };
  rechazadas: { cantidad: number; monto: number };
  enProceso: number;
  ajustes: { cantidad: number; neto: number };
}

@Component({
  selector: 'app-operations-history',
  templateUrl: './operations-history.component.html',
  styleUrls: ['./operations-history.component.sass'],
})
export class OperationsHistoryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly busqueda$ = new Subject<void>();
  private peticion?: Subscription;

  filtros: EstadoFiltros = this.filtrosVacios();
  catalogos: CatalogosHistorial = {
    grupos: [],
    estados: [],
    fuentes: [],
    tipos: [],
    monedas: [],
  };

  filas: FilaHistorial[] = [];
  pagina = 1;
  limite = 25;
  total = 0;
  totalPaginas = 1;
  periodoReal: { desde: string; hasta: string } | null = null;
  /** Totales de todo el resultado filtrado. `null` si el backend no los manda. */
  resumen: ResumenTotales | null = null;

  cargando = false;
  descargando: 'csv' | 'xlsx' | null = null;
  error = '';

  /** Fila abierta en el detalle. La llave es `fuente-id`. */
  expandida: string | null = null;
  /** En pantalla chica los filtros arrancan plegados. */
  filtrosAbiertos = false;
  /**
   * La pantalla se abrio desde una tarjeta del tablero. Cambia el aviso de
   * arriba: ahi el criterio es el del tablero, no el del historial completo.
   */
  vieneDelTablero = false;

  readonly opcionesLimite = [25, 50, 100, 200];
  readonly nombreGrupo = NOMBRE_GRUPO;
  readonly nombreFuente = NOMBRE_FUENTE;

  constructor(
    private readonly servicio: OperationsHistoryService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snack: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.servicio.catalogos().subscribe({
      next: (c) => {
        this.catalogos = c;
        this.cdr.detectChanges();
      },
      error: () => {},
    });

    // El texto libre no dispara una consulta por tecla.
    this.busqueda$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.aplicar());

    // Los filtros viven en la URL: asi el link es compartible, el back del
    // navegador funciona y el click desde el tablero es una navegacion normal.
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.desdeUrl(params));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.peticion?.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Estado <-> URL
  // ---------------------------------------------------------------------------

  private filtrosVacios(): EstadoFiltros {
    const hasta = this.hoyEnSantiago();
    return {
      desde: this.sumarDias(hasta, -29),
      hasta,
      usuario: '',
      operador: '',
      grupos: [],
      estados: [],
      fuentes: [],
      tipos: [],
      moneda: '',
      baseFecha: 'movimiento',
      orden: 'desc',
    };
  }

  /** Lee la URL y consulta. Es el unico camino que dispara una consulta. */
  private desdeUrl(params: Params): void {
    const lista = (valor: unknown): string[] =>
      valor ? String(valor).split(',').filter(Boolean) : [];

    const base = this.filtrosVacios();
    this.filtros = {
      desde: (params['desde'] as string) || base.desde,
      hasta: (params['hasta'] as string) || base.hasta,
      usuario: (params['usuario'] as string) || '',
      operador: (params['operador'] as string) || '',
      grupos: lista(params['grupos']),
      estados: lista(params['estados']),
      fuentes: lista(params['fuentes']),
      tipos: lista(params['tipos']),
      moneda: (params['moneda'] as string) || '',
      baseFecha: params['baseFecha'] === 'termino' ? 'termino' : 'movimiento',
      orden: params['orden'] === 'asc' ? 'asc' : 'desc',
    };
    this.pagina = Number(params['pagina']) > 0 ? Number(params['pagina']) : 1;
    this.limite = this.opcionesLimite.includes(Number(params['limite']))
      ? Number(params['limite'])
      : 25;
    this.vieneDelTablero = params['origen'] === 'tablero';

    this.consultar();
  }

  /** Escribe el estado en la URL. El `queryParams` de vuelta dispara la consulta. */
  private aUrl(pagina = this.pagina, reemplazar = true): void {
    const limpio = (valor: string | string[]) =>
      Array.isArray(valor) ? (valor.length ? valor.join(',') : null) : valor || null;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        desde: this.filtros.desde || null,
        hasta: this.filtros.hasta || null,
        usuario: limpio(this.filtros.usuario),
        operador: limpio(this.filtros.operador),
        grupos: limpio(this.filtros.grupos),
        estados: limpio(this.filtros.estados),
        fuentes: limpio(this.filtros.fuentes),
        tipos: limpio(this.filtros.tipos),
        moneda: limpio(this.filtros.moneda),
        baseFecha: this.filtros.baseFecha === 'termino' ? 'termino' : null,
        orden: this.filtros.orden === 'asc' ? 'asc' : null,
        pagina: pagina > 1 ? pagina : null,
        limite: this.limite !== 25 ? this.limite : null,
        origen: this.vieneDelTablero ? 'tablero' : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: reemplazar,
    });
  }

  // ---------------------------------------------------------------------------
  // Consulta
  // ---------------------------------------------------------------------------

  private consultar(): void {
    this.cargando = true;
    this.error = '';
    this.expandida = null;

    // Una consulta nueva cancela la anterior: si el operador escribe rapido,
    // la respuesta vieja puede llegar despues y pisar la buena.
    this.peticion?.unsubscribe();
    this.peticion = this.servicio.listar(this.comoFiltros()).subscribe({
      next: (pagina: PaginaHistorial) => {
        this.filas = pagina.datos;
        this.total = pagina.total;
        this.totalPaginas = pagina.totalPaginas;
        this.pagina = pagina.pagina;
        this.limite = pagina.limite;
        this.periodoReal = { desde: pagina.desde, hasta: pagina.hasta };
        this.resumen = pagina.totales ? this.resumir(pagina.totales) : null;
        this.cargando = false;
        this.cdr.detectChanges();
      },
      error: (e) => {
        this.filas = [];
        this.total = 0;
        this.totalPaginas = 1;
        this.resumen = null;
        this.error =
          e?.error?.message ??
          'No se pudo cargar el historial. Probá de nuevo en unos segundos.';
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Los totales del backend -> los cuatro numeros de la franja.
   *
   * Los montos suman solo CLP: mezclar CLP con USDT o VES en una cifra no
   * significa nada. `monto` llega siempre positivo; el sentido del ajuste lo
   * da su tipo.
   */
  private resumir(totales: TotalHistorial[]): ResumenTotales {
    const sumar = (lista: TotalHistorial[]) =>
      lista.reduce(
        (acc, t) => ({
          cantidad: acc.cantidad + t.cantidad,
          monto: acc.monto + (t.moneda === 'CLP' ? t.monto : 0),
        }),
        { cantidad: 0, monto: 0 },
      );

    const credito = sumar(totales.filter((t) => t.tipo === 'AJUSTE_CREDITO'));
    const debito = sumar(totales.filter((t) => t.tipo === 'AJUSTE_DEBITO'));

    return {
      cargas: sumar(totales.filter((t) => t.grupo === 'FONDEO')),
      salidas: sumar(
        totales.filter(
          (t) => !NO_SON_SALIDAS.includes(t.grupo) && t.estado === 'ENVIADA',
        ),
      ),
      rechazadas: sumar(totales.filter((t) => t.estado === 'RECHAZADA')),
      enProceso: totales
        .filter((t) => !ESTADOS_CERRADOS.includes(t.estado))
        .reduce((n, t) => n + t.cantidad, 0),
      ajustes: {
        cantidad: credito.cantidad + debito.cantidad,
        neto: credito.monto - debito.monto,
      },
    };
  }

  private comoFiltros(): FiltrosHistorial {
    return {
      ...this.filtros,
      usuario: this.filtros.usuario.trim() || undefined,
      operador: this.filtros.operador.trim() || undefined,
      moneda: this.filtros.moneda || undefined,
      pagina: this.pagina,
      limite: this.limite,
    };
  }

  // ---------------------------------------------------------------------------
  // Acciones de la pantalla
  // ---------------------------------------------------------------------------

  /** Un filtro cambio: vuelve a la pagina 1 y consulta. */
  aplicar(): void {
    this.aUrl(1);
  }

  /** El texto libre pasa por el debounce antes de consultar. */
  alEscribir(): void {
    this.busqueda$.next();
  }

  limpiar(): void {
    this.filtros = this.filtrosVacios();
    this.vieneDelTablero = false;
    this.aplicar();
  }

  /** Vuelve a consultar sin cambiar nada (boton "Actualizar"). */
  recargar(): void {
    this.consultar();
  }

  /** Atajos de periodo. `dias = 1` es hoy; `dias = 7`, los ultimos 7 dias. */
  ultimosDias(dias: number): void {
    const hasta = this.hoyEnSantiago();
    this.filtros.hasta = hasta;
    this.filtros.desde = this.sumarDias(hasta, -(dias - 1));
    this.aplicar();
  }

  ayer(): void {
    const dia = this.sumarDias(this.hoyEnSantiago(), -1);
    this.filtros.desde = dia;
    this.filtros.hasta = dia;
    this.aplicar();
  }

  /** Atajos de estado: solo rellenan el filtro de estado que ya existe. */
  soloRechazadas(): void {
    this.filtros.estados = ['RECHAZADA'];
    this.aplicar();
  }

  soloEnProceso(): void {
    this.filtros.estados = this.catalogos.estados.filter(
      (e) => !ESTADOS_CERRADOS.includes(e),
    );
    this.aplicar();
  }

  /** Copia un dato del detalle sin abrir ni cerrar la fila. */
  async copiar(texto: string | null, evento: Event): Promise<void> {
    evento.stopPropagation();
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
      this.snack.open('Copiado.', 'Cerrar', { duration: 2000 });
    } catch {
      this.snack.open('No se pudo copiar. Seleccioná el texto a mano.', 'Cerrar', {
        duration: 4000,
      });
    }
  }

  irAPagina(numero: number): void {
    const destino = Math.min(Math.max(1, numero), this.totalPaginas);
    if (destino === this.pagina) return;
    this.aUrl(destino);
    // El scroll vuelve arriba: en el celular, si no, la pagina nueva aparece
    // por la mitad y parece que no paso nada.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cambiarLimite(nuevo: number): void {
    this.limite = Number(nuevo);
    this.aplicar();
  }

  /**
   * Suelta el criterio del tablero y muestra todo el movimiento del periodo.
   *
   * El tablero cuenta solo lo ENVIADA con `fecha_termino`; el historial
   * completo suma lo rechazado, lo anulado y lo que quedo a medias.
   */
  verTodoElMovimiento(): void {
    this.filtros.estados = [];
    this.filtros.baseFecha = 'movimiento';
    this.vieneDelTablero = false;
    this.aplicar();
  }

  alternarDetalle(fila: FilaHistorial): void {
    const llave = this.llave(fila);
    this.expandida = this.expandida === llave ? null : llave;
  }

  llave(fila: FilaHistorial): string {
    return `${fila.fuente}-${fila.id}`;
  }

  async descargar(formato: 'csv' | 'xlsx'): Promise<void> {
    if (this.descargando) return;
    this.descargando = formato;
    this.cdr.detectChanges();
    try {
      await this.servicio.descargar(this.comoFiltros(), formato);
      this.snack.open('Archivo descargado.', 'Cerrar', { duration: 4000 });
    } catch (e) {
      this.snack.open(await this.servicio.mensajeDeError(e), 'Cerrar', {
        duration: 9000,
      });
    } finally {
      this.descargando = null;
      this.cdr.detectChanges();
    }
  }

  // ---------------------------------------------------------------------------
  // Formato
  // ---------------------------------------------------------------------------

  /** `'2026-09-16T14:35:02Z'` -> `'16/09/2026 11:35'` (hora de Santiago). */
  fechaLegible(iso: string | null): string {
    if (!iso) return '—';
    const instante = new Date(iso);
    if (Number.isNaN(instante.getTime())) return '—';
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(instante);
  }

  /**
   * Solo la hora, en Santiago.
   *
   * Va aparte y no recortando `fechaLegible`: ese texto lleva coma entre fecha
   * y hora en `es-CL`, y el separador cambia entre versiones de ICU. Cortar por
   * posicion funciona hasta que deja de funcionar, en produccion.
   */
  horaLegible(iso: string | null): string {
    if (!iso) return '';
    const instante = new Date(iso);
    if (Number.isNaN(instante.getTime())) return '';
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(instante);
  }

  /** Solo el dia, para los encabezados de las tarjetas en el celular. */
  diaLegible(iso: string | null): string {
    if (!iso) return '—';
    const instante = new Date(iso);
    if (Number.isNaN(instante.getTime())) return '—';
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: 'short',
    }).format(instante);
  }

  monto(valor: number | null, moneda: string | null): string {
    if (valor === null || valor === undefined) return '—';
    const decimales = SIN_DECIMALES.includes(moneda ?? '') ? 0 : 2;
    const texto = valor.toLocaleString('es-CL', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
    return moneda ? `${texto} ${moneda}` : texto;
  }

  tasaLegible(valor: number | null): string {
    if (!valor) return '—';
    return valor.toLocaleString('es-CL', { maximumFractionDigits: 8 });
  }

  etiquetaGrupo(grupo: string): string {
    return NOMBRE_GRUPO[grupo] ?? grupo;
  }

  etiquetaFuente(fuente: string): string {
    return NOMBRE_FUENTE[fuente] ?? fuente;
  }

  /** En una carga de saldo la contraparte es quien transfirio, no un beneficiario. */
  etiquetaContraparte(fila: FilaHistorial): string {
    return fila.grupo === 'FONDEO' ? 'Transfirió' : 'Beneficiario';
  }

  /**
   * Cantidades con punto de miles, igual que los montos. El pipe `number` usa
   * el locale de la app (en-US) y escribia "11,209" al lado de "$ 6.132.934".
   */
  cantidad(valor: number): string {
    return (valor ?? 0).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  }

  /** Monto CLP de la franja de totales, con signo cuando importa. */
  montoClp(valor: number, conSigno = false): string {
    const texto = '$ ' + Math.abs(Math.round(valor)).toLocaleString('es-CL');
    if (!conSigno || valor === 0) return texto;
    return (valor > 0 ? '+' : '−') + texto;
  }

  /** Color del chip de estado. */
  claseEstado(estado: string): string {
    if (estado === 'ENVIADA') return 'chip-ok';
    if (estado === 'RECHAZADA') return 'chip-error';
    if (estado === 'ANULADA') return 'chip-anulada';
    return 'chip-pendiente';
  }

  /** Cuantos filtros hay puestos, para el contador del boton en el celular. */
  get filtrosActivos(): number {
    let n = 0;
    if (this.filtros.usuario.trim()) n++;
    if (this.filtros.operador.trim()) n++;
    if (this.filtros.moneda) n++;
    if (this.filtros.baseFecha === 'termino') n++;
    n += this.filtros.grupos.length ? 1 : 0;
    n += this.filtros.estados.length ? 1 : 0;
    n += this.filtros.fuentes.length ? 1 : 0;
    n += this.filtros.tipos.length ? 1 : 0;
    return n;
  }

  /** El backend recorto el periodo pedido. Se avisa en vez de mentir. */
  get periodoRecortado(): boolean {
    if (!this.periodoReal) return false;
    return (
      this.periodoReal.desde !== this.filtros.desde ||
      this.periodoReal.hasta !== this.filtros.hasta
    );
  }

  /** Numeros de pagina a mostrar: siempre pocos, para que entre en el celular. */
  get paginasVisibles(): number[] {
    const ventana = 2;
    const desde = Math.max(1, this.pagina - ventana);
    const hasta = Math.min(this.totalPaginas, this.pagina + ventana);
    const paginas: number[] = [];
    for (let i = desde; i <= hasta; i++) paginas.push(i);
    return paginas;
  }

  get primeraFila(): number {
    return this.total === 0 ? 0 : (this.pagina - 1) * this.limite + 1;
  }

  get ultimaFila(): number {
    return Math.min(this.pagina * this.limite, this.total);
  }

  /** Identidad para `*ngFor`: sin esto Angular redibuja la tabla entera. */
  porFila = (_: number, fila: FilaHistorial): string => this.llave(fila);

  // ---------------------------------------------------------------------------
  // Fechas del calendario de la mesa
  // ---------------------------------------------------------------------------

  /**
   * `'YYYY-MM-DD'` de hoy en Santiago.
   *
   * No se usa el reloj del navegador: un operador conectado desde Caracas o
   * mirando de madrugada veria un dia distinto al que cierra la mesa.
   */
  private hoyEnSantiago(): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Santiago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }

  private sumarDias(fecha: string, dias: number): string {
    const [a, m, d] = fecha.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
  }
}
